<script setup>
import { ref, computed, onMounted } from 'vue'
import { removePhoneNumberCountryPrefix } from '@dpgradio/creative'
import { useUserStore } from '../stores/user.js'

defineEmits(['confirm', 'cancel'])

const userStore = useUserStore()
const mobile = ref('')
const mobileFormState = ref('idle')
const step = ref('loading')

const originalMobile = computed(() => userStore.profile?.mobile)

onMounted(async () => {
  await userStore.initConfirmationRequirement()

  if (!userStore.shouldConfirmMobile) {
    step.value = 'confirm'
    return
  }

  if (originalMobile.value) {
    mobile.value = removePhoneNumberCountryPrefix(originalMobile.value)
  }
  step.value = 'mobile'
})

async function confirmMobile() {
  mobileFormState.value = 'loading'
  try {
    await userStore.confirmMobile(mobile.value)
    step.value = 'confirm'
  } catch {
    mobileFormState.value = 'error'
  }
}
</script>

<template>
  <div class="game-overlay">
    <div class="overlay-card">
      <template v-if="step === 'loading'">
        <p class="overlay-text">Laden...</p>
      </template>

      <template v-else-if="step === 'mobile'">
        <div v-if="originalMobile">
          <h2 class="overlay-title">Controleer je telefoonnummer</h2>
          <p class="overlay-text">
            Als je wint, moeten we je natuurlijk kunnen bereiken! Controleer hieronder of je telefoonnummer klopt.
          </p>
        </div>
        <div v-else>
          <h2 class="overlay-title">Mogen we je nummer?</h2>
          <p class="overlay-text">
            We hebben nog geen nummer van jou! Als je wint, moeten we je natuurlijk kunnen bereiken!
          </p>
        </div>
        <input v-model="mobile" class="mobile-input" type="tel" placeholder="Telefoonnummer" />
        <button class="cta-button" :disabled="mobileFormState === 'loading'" @click="confirmMobile">
          <span v-if="mobileFormState === 'idle'">Bevestig</span>
          <span v-if="mobileFormState === 'loading'">Laden...</span>
          <span v-if="mobileFormState === 'error'">Er is een fout opgetreden</span>
        </button>
        <button class="cancel-button" @click="$emit('cancel')">Annuleren</button>
      </template>

      <template v-else-if="step === 'confirm'">
        <div class="overlay-emoji">&#129321;</div>
        <h2 class="overlay-title">BEN JE ZEKER?</h2>
        <p class="overlay-text">Je hebt alle vakjes aangevinkt. Klaar om bingo te claimen?</p>
        <button class="cta-button bingo-button" @click="$emit('confirm')">BINGO!</button>
        <button class="cancel-button" @click="$emit('cancel')">Nee, nog even wachten</button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.game-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
}

.overlay-card {
  background: var(--bingo-gradient-ui);
  border-radius: 16px;
  padding: 40px 32px;
  text-align: center;
  max-width: 360px;
  width: 100%;
}

.overlay-emoji {
  font-size: 3rem;
  margin-bottom: 8px;
}

.overlay-title {
  font-family: var(--bingo-font-heading);
  text-transform: uppercase;
  color: white;
  font-size: 1.4rem;
  margin: 0 0 16px;
}

.overlay-text {
  font-family: var(--bingo-font-body);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 24px;
}

.mobile-input {
  display: block;
  width: 100%;
  font-family: var(--bingo-font-body);
  font-size: 1rem;
  padding: 12px;
  border: none;
  border-radius: 8px;
  margin-bottom: 16px;
  box-sizing: border-box;
  -webkit-appearance: none;
}

.cta-button {
  font-family: var(--bingo-font-heading);
  text-transform: uppercase;
  font-size: 1rem;
  font-weight: bold;
  color: var(--bingo-cta-text);
  background: white;
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  cursor: pointer;
  width: 100%;
  letter-spacing: 0.5px;
  transition: transform 0.15s ease;
}

.cta-button:hover:not(:disabled) {
  transform: scale(1.03);
}

.cta-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.bingo-button {
  font-size: 1.4rem;
  padding: 16px 48px;
  width: auto;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.bingo-button:hover {
  animation: none;
  transform: scale(1.08);
}

.cancel-button {
  font-family: var(--bingo-font-body);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  cursor: pointer;
  margin-top: 16px;
  padding: 8px;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cancel-button:hover {
  color: white;
}
</style>
